import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/shadcn/button';
import SiteFooter from './SiteFooter';
import lines from '../media/LandingPageNew/lines.png';
import topLeftStars from '../media/LandingPageNew/stars/top-left-stars.svg';
import topRightStars from '../media/LandingPageNew/stars/top-right-stars.svg';
import middleLeftStars from '../media/LandingPageNew/stars/middle-left-stars.svg';
import middleRightStars from '../media/LandingPageNew/stars/middle-right-stars.svg';
import bottomRightStars from '../media/LandingPageNew/stars/bottom-right-stars.svg';
import tundePhoto from '../media/About/tunde.jpg';
import seyePhoto from '../media/About/seye.jpg';

const fredoka = { fontFamily: 'Fredoka, sans-serif' };
const poppins = { fontFamily: 'Poppins, sans-serif' };

const wavyUnderline = {
  color: '#C4E57D',
  textDecoration: 'underline',
  textDecorationStyle: 'wavy',
  textDecorationColor: 'white',
  textDecorationThickness: '4px',
  textUnderlineOffset: '8px',
  fontWeight: '600',
};

// The three things Nova combines (from the "About Nova" copy), styled like the
// homepage's purple / green / coral stat cards.
const PILLARS = [
  { label: 'Live teaching', blurb: 'Real tutors who explain difficult ideas and help them click.', bg: '#765AD8', text: 'text-white' },
  { label: 'Independent practice', blurb: 'Practise between lessons, revisit what has been forgotten and build strong foundations.', bg: '#CDE990', text: 'text-black' },
  { label: 'Technology', blurb: 'Understand where each child is and adapt as they improve.', bg: '#FD8C7C', text: 'text-white' },
];

const FOUNDERS = [
  {
    name: 'Tunde',
    title: 'Co-Founder | Product & Technology',
    photo: tundePhoto,
    linkedin: 'https://www.linkedin.com/in/babatunde-akinboyewa-7783069b/',
    bio: [
      "Maths has always been a big part of Tunde's life. He studied Mathematics and Economics at LSE before building a career in finance, including roles at Deloitte and in investment banking. Along the way, he qualified as both a Chartered Accountant and CFA charterholder.",
      'At Nova, Tunde focuses mainly on the product and technology, as well as the overall experience for children and parents.',
      "Outside of Nova, he's a big football fan, enjoys keeping fit and will take almost any excuse to travel somewhere new.",
    ],
  },
  {
    name: 'Seye',
    title: 'Co-Founder | Learning & Curriculum',
    photo: seyePhoto,
    linkedin: 'https://www.linkedin.com/in/seye-akinboyewa-037074115/',
    bio: [
      "Seye co-founded Nova Learning and is responsible for how and what we teach, from designing lessons to shaping Nova's approach to learning.",
      'Maths has always been a big part of his life. After achieving an A* in Mathematics and an A in Further Mathematics at A-Level, he studied Accounting & Finance at the University of Warwick before going on to work in investment banking and financial strategy.',
      'At Nova, his focus is simple: making maths easier to understand, more engaging and helping children build real confidence in the subject.',
      "When he's not working on Nova, you'll usually find him in the gym, playing sport, or still thinking about Nova.",
    ],
  },
];

const LinkedInIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const FounderCard = ({ founder }) => (
  <div className="flex-1 bg-white rounded-3xl border border-[#EDE7FF] shadow-lg p-8 md:p-10 flex flex-col items-center text-center">
    <img
      src={founder.photo}
      alt={`${founder.name}, Co-founder of Nova`}
      className="w-36 h-36 md:w-44 md:h-44 rounded-full object-cover ring-4 ring-[#C4E57D] ring-offset-4 ring-offset-white mb-6"
    />
    <h3 className="text-2xl md:text-3xl text-[#2F1E6B] font-medium" style={fredoka}>
      {founder.name}
    </h3>
    <span
      className="mt-2 mb-6 inline-block bg-[#F1EEF7] text-[#5C3BD1] rounded-lg px-4 py-1 text-base font-medium"
      style={fredoka}
    >
      {founder.title}
    </span>
    <div className="space-y-4 text-left w-full flex-1">
      {founder.bio.map((paragraph) => (
        <p key={paragraph} className="text-gray-700 text-base leading-relaxed" style={poppins}>
          {paragraph}
        </p>
      ))}
    </div>
    <a
      href={founder.linkedin}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-8 inline-flex items-center gap-2 text-[#5C3BD1] hover:text-[#2F1E6B] transition-colors duration-200 font-medium"
      style={fredoka}
    >
      <LinkedInIcon />
      Connect with {founder.name} on LinkedIn
    </a>
  </div>
);

const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>About Nova | Nova Learning</title>
        <meta
          name="description"
          content="Nova was founded by two brothers to help every child get better at maths, combining live teaching, independent practice and technology."
        />
      </Helmet>

      {/* Hero */}
      <section className="relative w-full px-8 sm:px-12 md:px-6 lg:px-8 max-md:px-4 pt-6">
        <div
          className="relative w-full rounded-2xl overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #5C3BD1 0%, #2F1E6B 100%)' }}
        >
          <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${lines})` }} />
          <img src={topLeftStars} alt="" className="absolute top-4 left-4 pointer-events-none hidden md:block" />
          <img src={topRightStars} alt="" className="absolute top-4 right-4 pointer-events-none hidden md:block" />
          <img src={bottomRightStars} alt="" className="absolute bottom-4 right-4 pointer-events-none hidden md:block" />

          <div className="relative max-w-[1100px] mx-auto px-6 md:px-8 py-16 md:py-24 text-center">
            <span
              className="inline-block bg-white/15 text-white rounded-lg px-4 py-1 text-lg font-medium mb-6"
              style={fredoka}
            >
              About Nova
            </span>
            <h1 className="text-white text-4xl md:text-5xl lg:text-6xl leading-tight mb-6" style={{ ...fredoka, fontWeight: '500' }}>
              Meet the <span style={wavyUnderline}>founders</span>
            </h1>
            <p className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-3" style={{ ...poppins, color: 'rgba(255, 255, 255, 0.8)' }}>
              Nova was founded by two brothers who have always shared an interest in education, technology and building things.
            </p>
            <p className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed" style={{ ...poppins, color: 'rgba(255, 255, 255, 0.8)' }}>
              We bring quite different backgrounds to Nova, and each has shaped its own part of the programme.
            </p>
          </div>
        </div>
      </section>

      {/* Founder cards */}
      <section className="relative w-full px-8 sm:px-12 md:px-6 lg:px-8 max-md:px-4 py-12 md:py-16">
        <div className="max-w-[1100px] mx-auto flex flex-col lg:flex-row gap-6 lg:gap-8 items-stretch">
          {FOUNDERS.map((founder) => (
            <FounderCard key={founder.name} founder={founder} />
          ))}
        </div>
      </section>

      {/* About Nova */}
      <section className="relative w-full px-8 sm:px-12 md:px-6 lg:px-8 max-md:px-4 pb-12 md:pb-16">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl text-[#2F1E6B] font-medium mb-5" style={fredoka}>
              Helping every child get better at maths
            </h2>
            <p className="text-gray-700 text-base md:text-lg leading-relaxed" style={poppins}>
              We started Nova with a simple belief: every child can get better at maths with the right teaching, the right level of challenge and enough practice.
            </p>
          </div>
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
            <div className="flex-1 space-y-5">
              <p className="text-gray-700 text-base md:text-lg leading-relaxed" style={poppins}>
                For some children, that means filling in gaps that are making maths harder than it needs to be. For others, it means moving ahead, tackling more challenging topics and seeing just how far they can go.
              </p>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed" style={poppins}>
                Nova combines live teaching, independent practice and technology to help children learn at the level that's right for them, build confidence and make real progress.
              </p>
            </div>
            <div className="flex-1 w-full flex flex-col gap-4">
              {PILLARS.map((pillar) => (
                <div key={pillar.label} className={`rounded-2xl px-6 py-5 ${pillar.text}`} style={{ backgroundColor: pillar.bg }}>
                  <h3 className="text-xl md:text-2xl font-medium mb-1" style={fredoka}>{pillar.label}</h3>
                  <p className="text-sm md:text-base leading-relaxed opacity-90" style={poppins}>{pillar.blurb}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why we built Nova */}
      <section className="relative w-full px-8 sm:px-12 md:px-6 lg:px-8 max-md:px-4 pb-12 md:pb-16">
        <div className="relative bg-[#F1EEF7] rounded-2xl overflow-hidden">
          <img src={middleLeftStars} alt="" className="absolute top-6 left-6 pointer-events-none hidden md:block" />
          <img src={middleRightStars} alt="" className="absolute bottom-6 right-6 pointer-events-none hidden md:block" />
          <div className="relative max-w-[900px] mx-auto px-6 md:px-8 py-12 md:py-16">
            <h2 className="text-3xl md:text-4xl text-[#2F1E6B] font-medium mb-8 text-center" style={fredoka}>
              Why we built Nova
            </h2>
            <div className="space-y-5">
              <p className="text-gray-700 text-base md:text-lg leading-relaxed" style={poppins}>
                We wanted to take the things that make great tutoring effective and build a better learning experience around them.
              </p>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed" style={poppins}>
                A tutor can explain a difficult idea and help it click, but children also need to practise between lessons, revisit things they have forgotten and build strong foundations over time.
              </p>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed" style={poppins}>
                That's why we built Nova around both great teachers and technology. We want to understand where each child is, identify the areas holding them back and give them the opportunity to move forward when they're ready.
              </p>
              <p className="text-[#2F1E6B] text-base md:text-lg leading-relaxed border-l-4 border-[#A1CC3A] pl-5 font-medium" style={poppins}>
                Most importantly, we wanted to build something that children actually enjoy using. Because the more they want to learn and practise, the more progress they can make.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative w-full px-8 sm:px-12 md:px-6 lg:px-8 max-md:px-4 pb-16">
        <div className="bg-[#2F1E6B] rounded-2xl px-6 md:px-8 py-12 text-center">
          <h2 className="text-white text-3xl md:text-4xl font-medium mb-4" style={fredoka}>
            Ready to see how far your child can go?
          </h2>
          <p className="text-base md:text-lg mb-8 max-w-2xl mx-auto" style={{ ...poppins, color: 'rgba(255, 255, 255, 0.8)' }}>
            Book a free assessment and we'll find the level that's right for them.
          </p>
          <Button
            onClick={() => navigate('/register')}
            className="bg-[#C4E57D] border-2 border-[#9FD42A] text-black hover:bg-[#9FD42A] hover:border-[#9FD42A] rounded-3xl px-8 py-3 text-lg font-semibold max-md:h-12"
            style={fredoka}
          >
            Book Free Assessment
          </Button>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default AboutPage;
